import React, { useMemo } from 'react'
import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CFormInput,
  CFormSelect,
  CTooltip,
} from '@coreui/react-pro'
import {
  DISMISSAL_KEYS,
  DISMISSAL_OPTIONS,
  POSITION_META,
  YES_NO_KEYS,
  groupColumns,
  hasScores,
} from './scorecardConfig'
import './ScoreCard.css'

// The entry grid shared by every scorecard screen. Presentation only: the field
// name, default value and change handler it emits are exactly what the save
// payload is built from and must not change.
const ScorecardGrid = ({
  columns,
  rows,
  isMobile = false,
  readOnly = false,
  onChange,
  formatFantasyPoints,
  emptyLabel = 'No players in this squad yet.',
}) => {
  const groupedColumns = useMemo(() => groupColumns(columns), [columns])
  const flatColumns = useMemo(
    () => groupedColumns.flatMap((group) => group.columns),
    [groupedColumns],
  )

  const fantasyPointsLabel = (item) => {
    const value = item?.fantasy_points
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return null
    }
    return formatFantasyPoints ? formatFantasyPoints(Number(value)) : String(value)
  }

  // Read-only screens show the figure itself. A row of disabled input boxes
  // reads as "you may type here but can't", which is worse than plain text and
  // costs three times the width.
  const renderReadOnlyCell = (column, item) => {
    const attributeKey = column?.attribute_key
    const value = item?.[attributeKey]

    if (YES_NO_KEYS.includes(attributeKey)) {
      return <span className="sc-read">{Number(value) === 1 ? 'Yes' : 'No'}</span>
    }
    if (DISMISSAL_KEYS.includes(attributeKey)) {
      const option = DISMISSAL_OPTIONS.find((entry) => entry.value === value)
      return option && option.value ? (
        <span className="sc-read">{option.label}</span>
      ) : (
        <span className="sc-read sc-read--empty">&mdash;</span>
      )
    }
    if (value === null || value === undefined || value === '') {
      return <span className="sc-read sc-read--empty">&mdash;</span>
    }
    return <span className="sc-read">{value}</span>
  }

  const renderStatCell = (column, item, rowIndex) => {
    if (readOnly) return renderReadOnlyCell(column, item)

    const attributeKey = column?.attribute_key
    const name = `data[${rowIndex}]${attributeKey}`

    if (YES_NO_KEYS.includes(attributeKey)) {
      return (
        <CFormSelect
          id={attributeKey}
          name={name}
          defaultValue={item[attributeKey]}
          onChange={onChange}
          className="sc-select"
        >
          <option value={0}>No</option>
          <option value={1}>Yes</option>
        </CFormSelect>
      )
    }

    if (DISMISSAL_KEYS.includes(attributeKey)) {
      return (
        <CFormSelect
          id={attributeKey}
          name={name}
          defaultValue={item[attributeKey]}
          onChange={onChange}
          className="sc-select sc-select--wide"
        >
          {DISMISSAL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </CFormSelect>
      )
    }

    return (
      <CFormInput
        id={attributeKey}
        name={name}
        type="number"
        step="any"
        inputMode="decimal"
        defaultValue={item[attributeKey]}
        onChange={onChange}
        // Select-on-focus so typing replaces the old figure instead of appending
        // to it, and blur-on-wheel so scrolling the grid can never silently
        // change a score.
        onFocus={(event) => event.target.select()}
        onWheel={(event) => event.target.blur()}
        className="sc-input"
      />
    )
  }

  if (!rows || rows.length === 0) {
    return <div className="text-medium-emphasis py-4 text-center">{emptyLabel}</div>
  }

  if (isMobile) {
    return (
      <CAccordion alwaysOpen>
        {rows.map((item, rowIndex) => {
          const position = POSITION_META[item?.player_position]
          const points = fantasyPointsLabel(item)
          return (
            <CAccordionItem itemKey={rowIndex} key={item?.id ?? rowIndex}>
              <CAccordionHeader>
                <span className="d-flex align-items-center gap-2">
                  <span className="fw-semibold">{item.player_name}</span>
                  {position && <span className="sc-pos-badge">{position.label}</span>}
                  <span className={`sc-fp-chip${points ? '' : ' sc-fp-chip--empty'}`}>
                    {points ?? '—'}
                  </span>
                </span>
              </CAccordionHeader>
              <CAccordionBody>
                <div className="sc-player-panel-group">
                  {groupedColumns.map((group) => (
                    <div key={group.key}>
                      <h6 className="sc-mobile-group-title">{group.label}</h6>
                      {group.columns.map((column) => (
                        <div className="sc-mobile-field" key={column?.attribute_key}>
                          <span className="sc-mobile-label">
                            <span className="sc-mobile-label-code">{column?.label}</span>
                            <span className="sc-mobile-label-full">{column?.full_name}</span>
                          </span>
                          {renderStatCell(column, item, rowIndex)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </CAccordionBody>
            </CAccordionItem>
          )
        })}
      </CAccordion>
    )
  }

  return (
    <>
      <div className="sc-grid-wrap">
        <table className="sc-grid">
          <thead>
            <tr>
              <th colSpan={3} className="sc-sticky sc-group-spacer" />
              {groupedColumns.map((group) => (
                <th
                  key={group.key}
                  colSpan={group.columns.length}
                  className={`sc-group-cell sc-group--${group.key}`}
                >
                  {group.label}
                </th>
              ))}
            </tr>
            <tr>
              <th className="sc-col-cell sc-sticky sc-col-player">
                <div className="sc-col-code">Player</div>
              </th>
              <th className="sc-col-cell sc-sticky sc-col-pos">
                <div className="sc-col-code">Pos</div>
              </th>
              <th className="sc-col-cell sc-sticky sc-col-fp">
                <CTooltip content="Fantasy points">
                  <div className="sc-col-code sc-col-code--hint">FP</div>
                </CTooltip>
              </th>
              {flatColumns.map((column) => (
                <th key={column?.attribute_key} className="sc-col-cell">
                  <CTooltip content={column?.full_name || column?.label}>
                    <div className="sc-col-code sc-col-code--hint">{column?.label}</div>
                  </CTooltip>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item, rowIndex) => {
              const position = POSITION_META[item?.player_position]
              const points = fantasyPointsLabel(item)
              const started = hasScores(item, columns)
              return (
                <tr key={item?.id ?? rowIndex}>
                  <td
                    className={`sc-cell sc-sticky sc-col-player${started ? '' : ' sc-not-started'}`}
                  >
                    <span className="sc-player-name" title={item.player_name}>
                      {item.player_name}
                    </span>
                  </td>
                  <td className="sc-cell sc-sticky sc-col-pos">
                    {position && <span className="sc-pos-badge">{position.label}</span>}
                  </td>
                  <td className="sc-cell sc-sticky sc-col-fp">
                    <span className={`sc-fp-chip${points ? '' : ' sc-fp-chip--empty'}`}>
                      {points ?? '—'}
                    </span>
                  </td>
                  {flatColumns.map((column) => (
                    <td className="sc-cell" key={column?.attribute_key}>
                      {renderStatCell(column, item, rowIndex)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="sc-legend-note">
        <span>
          <span className="sc-legend-swatch" />
          Amber marker = no scores saved for that player yet
        </span>
        <span>Player, position and fantasy points stay in view as you scroll across.</span>
      </div>
    </>
  )
}

export default ScorecardGrid
